/**
 * Utility functions to obfuscate phone and WhatsApp numbers
 * This prevents AI crawlers from easily extracting contact information
 * from the initial HTML source
 * 
 * Uses base64 encoding and dynamic decoding to hide numbers from crawlers
 */

import { useState, useEffect } from 'react';

// Encoded phone number (917574082582 -> base64)
// This is the base64 encoding of "917574082582"
const ENCODED_PHONE_DIGITS = 'OTE3NTc0MDgyNTgy';

// Check if we're in a pre-rendering environment (Puppeteer/Headless Chrome)
const isPreRendering = () => {
  if (typeof window === 'undefined') return true;
  // Puppeteer uses HeadlessChrome user agent
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    return navigator.userAgent.includes('HeadlessChrome') || 
           navigator.userAgent.includes('Chrome-Lighthouse');
  }
  return false;
};

// Decode phone digits
const decodePhoneDigits = () => {
  // Don't decode during SSR or pre-rendering
  if (typeof window === 'undefined' || isPreRendering()) return '';
  try {
    return atob(ENCODED_PHONE_DIGITS);
  } catch (e) {
    return '';
  }
};

// Get phone number (formatted: +91 75740 82582)
export const getPhoneNumber = () => {
  const digits = decodePhoneDigits();
  if (!digits) return ''; // Return empty if not decoded
  if (digits.startsWith('91') && digits.length === 12) {
    // Format as +91 XXXXX XXXXX (5-5 split after country code)
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  return `+${digits}`;
};

// Get phone number without formatting (for tel: links: +917574082582)
export const getPhoneNumberRaw = () => {
  const digits = decodePhoneDigits();
  if (!digits) return ''; // Return empty if not decoded
  return `+${digits}`;
};

// Get phone number without country code (for WhatsApp: 917574082582)
export const getPhoneNumberForWhatsApp = () => {
  return decodePhoneDigits(); // Returns empty string if not decoded
};

// Get WhatsApp URL
export const getWhatsAppUrl = (message = "Hi! I'm interested in your web development services. Can you help me get started?") => {
  const phone = getPhoneNumberForWhatsApp();
  if (!phone) return ''; // Return empty if not decoded
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
};

// Get tel: link
export const getTelLink = () => {
  const phone = getPhoneNumberRaw();
  if (!phone) return ''; // Return empty if not decoded
  return `tel:${phone}`;
};

// React hook to get contact info (decodes after mount to avoid initial HTML exposure)
export const useContactInfo = () => {
  const [contactInfo, setContactInfo] = useState({
    phone: '',
    phoneLink: '',
    whatsapp: '',
    phoneRaw: '',
    phoneWhatsApp: ''
  });

  useEffect(() => {
    // Decode after component mounts (not in initial HTML)
    setContactInfo({
      phone: getPhoneNumber(),
      phoneLink: getTelLink(),
      whatsapp: getWhatsAppUrl(),
      phoneRaw: getPhoneNumberRaw(),
      phoneWhatsApp: getPhoneNumberForWhatsApp()
    });
  }, []);

  return contactInfo;
};

